import type { GetServerSideProps } from 'next'
import type { ParsedUrlQuery } from 'querystring'

import { post } from 'src/functions/fetch'
import { saveUrl } from 'src/functions/regex'
import { productVariantUrl } from 'src/functions/productUrl'
import Product from 'src/pages/Product/index'
import { isProduct, type tProduct } from 'src/shared/frontendTypes/product'
import config from 'src/config'
import { fetchSiteShopSettings } from 'src/pageBuilder/sitePageBuilder'
import type { MenuPageBuilder } from 'src/shared/dbTypes/menuPageBuilder'
import type { LandingPageProductInquirySettings } from 'src/shared/dbTypes/landingPageSettings'
import type { LandingPageImageAiGenerated } from 'src/functions/landingPageImageAiGenerated'

interface Params extends ParsedUrlQuery {
  pid: string
  alias: string
  variant: string
}

interface Props {
  product: tProduct
  initialVariantKey: string
  sitePageBuilder?: MenuPageBuilder
  productInquiry?: LandingPageProductInquirySettings
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const ProductVariantPage: React.FC<Props> = ({ product, initialVariantKey, landingPageImageAiGenerated }) => (
  <Product product={product} initialVariantKey={initialVariantKey} landingPageImageAiGenerated={landingPageImageAiGenerated} />
)

export default ProductVariantPage

export const getServerSideProps: GetServerSideProps<Props, Params> = async ({ params }) => {
  const { pid, alias, variant } = params as Params
  const variantKey = String(variant)
  const menuId = Number(process.env.NEXT_PUBLIC_MENU)
  // Settle early failures even when the product later returns a redirect or 404.
  const siteSettingsPromise = fetchSiteShopSettings({ local: true }).then(
    value => ({ ok: true as const, value }),
    error => ({ ok: false as const, error }),
  )
  let data: unknown = null

  for (let attempt = 0; attempt < 3; attempt++) {
    data = await post({
      url: `product/${pid}`,
      body: {
        ...config.shop,
        ...(Number.isFinite(menuId) ? { menu: menuId } : {}),
      },
      local: true,
    })

    if (isProduct(data)) break
    if (attempt < 2) await wait(250)
  }

  if (!isProduct(data) || !data.combinations.some(combination => combination.key === variantKey)) {
    return { notFound: true }
  }

  const canonicalAlias = saveUrl(data.path || String(data.id))
  if (canonicalAlias !== alias) {
    return {
      redirect: {
        destination: productVariantUrl(data.id, canonicalAlias, variantKey),
        permanent: true,
      },
    }
  }

  const siteSettingsResult = await siteSettingsPromise
  if (!siteSettingsResult.ok) throw siteSettingsResult.error
  const siteSettings = siteSettingsResult.value

  return {
    props: {
      product: data,
      initialVariantKey: variantKey,
      ...(siteSettings.sitePageBuilder ? { sitePageBuilder: siteSettings.sitePageBuilder } : {}),
      ...(siteSettings.productInquiry ? { productInquiry: siteSettings.productInquiry } : {}),
      ...(siteSettings.landingPageImageAiGenerated ? { landingPageImageAiGenerated: siteSettings.landingPageImageAiGenerated } : {}),
    },
  }
}
